import {
  Button,
  CustomSelect,
  Skeleton,
  TextArea,
  toast,
} from "../../../components";
import { RootState } from "../../../store";
import React, { useState, useEffect } from "react";
import { connect, ConnectedProps } from "react-redux";
import { aiService, deleteTranscript } from "@/store/app/app.actions";
import { Form } from "react-bootstrap";
import Transcribe from "../../../assets/icons/svg/transcribe.svg?react";
import SectionHeader from "@/components/Layout/SectionHeader";

// interface TranscriptItem {
//   time: string;
//   text: string;
// }

interface TranscriptCue {
  start: string;
  end: string;
  text: string;
}

interface MediaTranscriptProps {
  onSubmit?: (language: string) => void;
}

const parseVttCues = (vtt: string): TranscriptCue[] => {
  if (!vtt) return [];
  const cues: TranscriptCue[] = [];
  const blocks = vtt.replace(/\r/g, "").split("\n\n");

  blocks.forEach((block) => {
    const lines = block.split("\n").filter(Boolean);
    const timingLine = lines.find((line) => line.includes("-->"));
    if (!timingLine) return;

    const [start, end] = timingLine.split("-->").map((part) => part.trim());
    const text = lines
      .slice(lines.indexOf(timingLine) + 1)
      .join(" ")
      .trim();

    if (text) {
      cues.push({ start, end, text });
    }
  });

  return cues;
};

const cuesToTimelineText = (cues: TranscriptCue[]): string =>
  cues.map((cue) => `${cue.start} - ${cue.end}\n${cue.text}`).join("\n\n");

const MediaTranscript = ({
  Language_Options,
  aiService,
  currentMediaDetailsForEdit,
  deleteTranscript,
}: Props & MediaTranscriptProps) => {
  // const [language, setLanguage] = useState<string>('');
  // const [error, setError] = useState<string>('');
  const [transcript, setTranscript] = useState<string>("");
  const [cues, setCues] = useState<TranscriptCue[]>([]);
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const isGenerating = currentMediaDetailsForEdit?.transcript === 1;
  const hasTranscript = currentMediaDetailsForEdit?.transcript === 2;
  const hasError = currentMediaDetailsForEdit?.transcript === -1;

  // const handleLanguageChange = (newValue: any) => {
  //   setLanguage(newValue.value);
  //   setError('');
  // };

  const handleGenerateTranscript = () => {
    // if (!language) {
    //   setError('Please select a language');
    //   return;
    // }
    aiService({
      target_language: "English",
      service: "transcript",
      id: currentMediaDetailsForEdit.id,
    });
  };

  useEffect(() => {
    if (hasTranscript) {
      setIsLoading(true);
      aiService({
        service: "transcript",
        id: currentMediaDetailsForEdit.id,
      })
        .then((response) => {
          setTranscript(response.transcription);
          setCues(parseVttCues(response.transcription_vtt));
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [hasTranscript, currentMediaDetailsForEdit?.id]);

  const handleCopyTranscript = () => {
    const textToCopy = showTimeline ? cuesToTimelineText(cues) : transcript;
    navigator.clipboard.writeText(textToCopy);
  };

  const handleDownloadTranscript = () => {
    const textToCopy = transcript;
    const blob = new Blob([textToCopy], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${currentMediaDetailsForEdit.title} transcript.txt`;
    a.click();
  };

  const handleRetryTranscript = () => {
    deleteTranscript({
      id: currentMediaDetailsForEdit.id,
    }).then(() => {
      aiService({
        service: "transcript",
        id: currentMediaDetailsForEdit.id,
      });
    });
  };

  return (
    <div className="w-100 p-3">
      <SectionHeader
        title="Transcript"
        subtitle="Auto-generated transcript with timestamps"
      >
        {hasTranscript && !hasError && !isLoading && (
          <div className="d-flex align-items-center gap-4 flex-shrink-0">
            <Form.Check
              type="switch"
              id="timeline-switch"
              label="with Timeline"
              checked={showTimeline}
              onChange={() => setShowTimeline(!showTimeline)}
              className="mt-2"
            />
            <i className="bi bi-copy fs-5" onClick={handleCopyTranscript}></i>
            <i
              className="bi bi-download fs-5"
              onClick={handleDownloadTranscript}
            ></i>
          </div>
        )}
        {hasError && !isLoading && (
          <Button type="outline-primary" onClick={handleRetryTranscript}>
            Regenerate
          </Button>
        )}
      </SectionHeader>
        <div
          className={`d-flex flex-column gap-3 rounded-3 ${
            hasTranscript ? "align-items-stretch" : "align-items-center justify-content-center"
          }`}
          style={{ height: "60vh", backgroundColor: "#f1f1f1" }}>
        {isGenerating || isLoading ? (
          <Skeleton type="table" count={4} />
        ) : hasTranscript ? (
          <div
            className="transcript-list w-100"
            style={{ flex: 1, minHeight: 0 }}
          >
            {showTimeline && cues.length > 0 ? (
              <div className="d-flex flex-column gap-2">
                {cues.map((cue, index) => (
                  <div
                    key={`${cue.start}-${index}`}
                    className="d-flex align-items-start gap-3 p-2 border-bottom"
                  >
                    <span
                      className="text-muted flex-shrink-0"
                      style={{ minWidth: "160px" }}
                    >
                      {cue.start} - {cue.end}
                    </span>
                    <span className="flex-grow-1">{cue.text}</span>
                  </div>
                ))}
              </div>
            ) : (
              <TextArea
                className=""
                value={transcript}
                readOnly={true}
                label="Transcript"
                showRequired={false}
                onChange={() => {}}
                onBlur={() => {}}
                name="transcript"
                autoExpandRows={true}
                style={{ width: "100%", height: "100%" }}
                hideLabel={true}
              />
            )}
          </div>
        ) : (
          <div className="d-flex justify-content-center flex-column align-items-center w-100 h-100 gap-3">
              <Transcribe style={{ width: "40px", height: "40px" }} />
              <span className="mb-2">No transcription available.</span>
              <div className="d-flex align-items-center justify-content-center gap-3 w-100">
                {/* <div style={{ flex: 1 }}>
                <CustomSelect
                  name="language"
                  placeholder="Select Language"
                  value={language}
                  options={Language_Options}
                  handleChange={handleLanguageChange}
                  label=""
                  error={error}
                  touched={!!error}
                />
              </div> */}
                <button
                  onClick={handleGenerateTranscript}
                  className="btn btn-primary px-4"
                  disabled={isGenerating || hasError || isLoading}
                  // disabled={!language || isGenerating}
                >
                  {isGenerating ? "Generating..." : "Generate"}
                </button>
              </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  Language_Options: state.app.metaLanguage,
  currentMediaDetailsForEdit: state.media.mediaDetails.media,
});

const mapDispatch = {
  aiService,
  deleteTranscript,
};

const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector>;
export default connector(MediaTranscript);
